import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { api } from '../services/api';

const UI_CONFIG_KEY = 'ui';

type UiSettings = {
  show_test_dhis2_tab: boolean;
  /**
   * List of config IDs for which the Diagnosis section is visible.
   * Empty array  → hidden for every config.
   * null/missing → falls back to diagnosisEnabledByDefault.
   */
  diagnosis_enabled_config_ids: string[] | null;
  /**
   * When no allowlist is configured, should the section be shown?
   * Defaults to true so existing deployments are unaffected.
   */
  diagnosis_enabled_by_default: boolean;
};

const defaultUi: UiSettings = {
  show_test_dhis2_tab: true,
  diagnosis_enabled_config_ids: null,
  diagnosis_enabled_by_default: true,
};

type UiSettingsContextValue = {
  showTestDhis2Tab: boolean;
  setShowTestDhis2Tab: (value: boolean) => void;
  diagnosisEnabledConfigIds: string[] | null;
  diagnosisEnabledByDefault: boolean;
  setDiagnosisPolicy: (configIds: string[] | null, enabledByDefault: boolean) => void;
  /** Derive from context: is Diagnosis visible for the given configId? */
  isDiagnosisEnabled: (activeConfigId: string | null | undefined) => boolean;
  saveUiSettings: () => Promise<void>;
  loadUiSettings: () => Promise<void>;
};

const UiSettingsContext = createContext<UiSettingsContextValue | null>(null);

export function UiSettingsProvider({ children }: { children: ReactNode }) {
  const [showTestDhis2Tab, setShowTestDhis2TabState] = useState(defaultUi.show_test_dhis2_tab);
  const [diagnosisEnabledConfigIds, setDiagnosisEnabledConfigIds] = useState<string[] | null>(
    defaultUi.diagnosis_enabled_config_ids,
  );
  const [diagnosisEnabledByDefault, setDiagnosisEnabledByDefault] = useState(
    defaultUi.diagnosis_enabled_by_default,
  );

  const buildPayload = useCallback(
    () => ({
      show_test_dhis2_tab: showTestDhis2Tab,
      diagnosis_enabled_config_ids: diagnosisEnabledConfigIds,
      diagnosis_enabled_by_default: diagnosisEnabledByDefault,
    }),
    [showTestDhis2Tab, diagnosisEnabledConfigIds, diagnosisEnabledByDefault],
  );

  const loadUiSettings = useCallback(async () => {
    try {
      const res = await api.get<{ config_key: string; config_value_json: Record<string, unknown> }>(
        `/system-config/${UI_CONFIG_KEY}`,
      );
      const json = res.data?.config_value_json as Partial<UiSettings> | undefined;
      setShowTestDhis2TabState(json?.show_test_dhis2_tab ?? defaultUi.show_test_dhis2_tab);
      const rawIds = json?.diagnosis_enabled_config_ids;
      setDiagnosisEnabledConfigIds(
        Array.isArray(rawIds) ? rawIds.map(String) : defaultUi.diagnosis_enabled_config_ids,
      );
      setDiagnosisEnabledByDefault(
        typeof json?.diagnosis_enabled_by_default === 'boolean'
          ? json.diagnosis_enabled_by_default
          : defaultUi.diagnosis_enabled_by_default,
      );
    } catch {
      // Network error or 404 → stay on defaults (safe fallback)
      setShowTestDhis2TabState(defaultUi.show_test_dhis2_tab);
      setDiagnosisEnabledConfigIds(defaultUi.diagnosis_enabled_config_ids);
      setDiagnosisEnabledByDefault(defaultUi.diagnosis_enabled_by_default);
    }
  }, []);

  const saveUiSettings = useCallback(async () => {
    await api.put(`/system-config/${UI_CONFIG_KEY}`, {
      config_value_json: buildPayload(),
    });
  }, [buildPayload]);

  useEffect(() => {
    void loadUiSettings();
  }, [loadUiSettings]);

  const setShowTestDhis2Tab = useCallback((value: boolean) => {
    setShowTestDhis2TabState(value);
  }, []);

  const setDiagnosisPolicy = useCallback(
    (configIds: string[] | null, enabledByDefault: boolean) => {
      setDiagnosisEnabledConfigIds(configIds);
      setDiagnosisEnabledByDefault(enabledByDefault);
    },
    [],
  );

  /**
   * Resolution logic:
   *  1. If allowlist is set and non-empty → only those config IDs can see Diagnosis.
   *  2. If allowlist is null              → fall back to diagnosisEnabledByDefault.
   *  3. If allowlist is [] (empty)        → hidden for everyone (admin explicitly cleared it).
   */
  const isDiagnosisEnabled = useCallback(
    (activeConfigId: string | null | undefined): boolean => {
      if (diagnosisEnabledConfigIds === null) return diagnosisEnabledByDefault;
      if (diagnosisEnabledConfigIds.length === 0) return false;
      if (!activeConfigId) return diagnosisEnabledByDefault;
      return diagnosisEnabledConfigIds.includes(activeConfigId);
    },
    [diagnosisEnabledConfigIds, diagnosisEnabledByDefault],
  );

  return (
    <UiSettingsContext.Provider
      value={{
        showTestDhis2Tab,
        setShowTestDhis2Tab,
        diagnosisEnabledConfigIds,
        diagnosisEnabledByDefault,
        setDiagnosisPolicy,
        isDiagnosisEnabled,
        saveUiSettings,
        loadUiSettings,
      }}
    >
      {children}
    </UiSettingsContext.Provider>
  );
}

export function useUiSettings(): UiSettingsContextValue {
  const ctx = useContext(UiSettingsContext);
  if (!ctx) throw new Error('useUiSettings must be used within UiSettingsProvider');
  return ctx;
}
